import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { adminNav } from '@/lib/navConfig';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { FileText, Truck, Receipt, Printer, Search, Trash2, Package, AlertTriangle, Filter } from 'lucide-react';
import api, { fmtIDR, fmtDate, API } from '@/lib/api';
import { toast } from 'sonner';
import { openPrintWindow } from '@/pages/owner/OwnerPrintTemplates';
import { DeliveryBatchesSection } from '@/components/transactions/TransactionsHistory';
import PrintBatchDialog from '@/components/documents/PrintBatchDialog';
import { useIncompleteDeliveries } from '@/hooks/useIncompleteDeliveries';

export default function AdminDocuments({ nav, title = 'Cetak Dokumen (SO / Surat Jalan / Invoice)' } = {}) {
  const [txs, setTxs] = useState([]);
  const [query, setQuery] = useState('');
  const [deliveryFor, setDeliveryFor] = useState(null);
  // Transaksi yang sedang dibuka dialog "Kelola Surat Jalan" (batch split).
  const [manageBatchTx, setManageBatchTx] = useState(null);
  // Dialog cetak per batch (unified untuk SO/SJ/Invoice + PDF/Dot Matrix)
  const [printDialog, setPrintDialog] = useState(null); // { tx, docType, useDotMatrix }
  // Filter: tampilkan hanya transaksi yang punya batch belum lengkap info kirim
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  // Notif batch belum lengkap (>24 jam)
  const { count: incompleteCount, txCount: incompleteTxCount, transactions: incompleteTxs } = useIncompleteDeliveries(24, { silent: true });
  const incompleteTxIds = useMemo(() => new Set((incompleteTxs || []).map((t) => t.transaction_id)), [incompleteTxs]);

  // Shortcut untuk buka dialog cetak per batch
  const openPrint = (tx, docType, useDotMatrix = false) => {
    setPrintDialog({ tx, docType, useDotMatrix });
  };

  const load = async () => {
    const { data } = await api.get('/transactions?limit=500');
    setTxs(data);
  };
  useEffect(() => { load(); }, []);

  /** Hitung sisa belum dikirim (Total order − sudah dikirim) untuk indicator kolom SJ. */
  const computeRemaining = (t) => {
    const batches = t.delivery_batches || [];
    if (batches.length === 0) return null; // legacy tx
    const shippedMap = {};
    batches.forEach((b) => (b.items || []).forEach((it) => {
      shippedMap[it.product_id] = (shippedMap[it.product_id] || 0) + Number(it.quantity || 0);
    }));
    let totalOrder = 0, totalShipped = 0, itemsRemaining = 0;
    (t.items || []).forEach((it) => {
      const q = Number(it.quantity || 0);
      const s = Number(shippedMap[it.product_id] || 0);
      totalOrder += q;
      totalShipped += Math.min(s, q);
      if (q - s > 0) itemsRemaining += 1;
    });
    return {
      totalOrder,
      totalShipped,
      totalRemaining: Math.max(0, totalOrder - totalShipped),
      itemsRemaining,
      batchesCount: batches.length,
    };
  };

  const filtered = useMemo(() => {
    let list = txs;
    if (showIncompleteOnly) {
      list = list.filter((t) => incompleteTxIds.has(t.id));
    }
    if (!query) return list;
    const q = query.toLowerCase();
    return list.filter((t) =>
      (t.invoice_no || '').toLowerCase().includes(q)
      || (t.customer_name || '').toLowerCase().includes(q)
      || (t.customer_company || '').toLowerCase().includes(q)
      || (t.so_no || '').toLowerCase().includes(q)
      || (t.do_no || '').toLowerCase().includes(q)
    );
  }, [txs, query, showIncompleteOnly, incompleteTxIds]);

  const download = async (tx, type) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/transactions/${tx.id}/document/${type}.pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Gagal cetak dokumen');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const filenames = { 'so': `SO-${tx.invoice_no}.pdf`, 'delivery-order': `SuratJalan-${tx.invoice_no}.pdf`, 'invoice': `Invoice-${tx.invoice_no}.pdf` };
      a.href = url; a.download = filenames[type]; a.click();
      URL.revokeObjectURL(url);
      toast.success('Dokumen berhasil diunduh');
      load();
    } catch (e) { toast.error(e.message); }
  };

  /** Cetak Dot Matrix — fetch teks rendered lalu buka jendela print. */
  const printDotMatrix = async (tx, docType) => {
    // docType: 'so' | 'delivery_order' | 'invoice'
    try {
      const res = await api.get(`/transactions/${tx.id}/print/${docType}`, { responseType: 'text' });
      const orient = (res.headers?.['x-print-orientation'] || res.headers?.get?.('x-print-orientation') || 'portrait').toLowerCase();
      openPrintWindow(res.data, `${docType.toUpperCase()} - ${tx.invoice_no}`, orient);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memuat template cetak');
    }
  };

  const handleDMPrint = (tx, docType) => {
    // Untuk Surat Jalan, wajib ada info pengiriman dulu
    if (docType === 'delivery_order' && (!tx.delivery_address || !tx.driver_name || !tx.vehicle_plate)) {
      setDeliveryFor({ ...tx, _dmType: 'delivery_order' });
      return;
    }
    printDotMatrix(tx, docType);
  };

  const handleDO = (tx) => {
    if (!tx.delivery_address || !tx.driver_name || !tx.vehicle_plate) {
      setDeliveryFor(tx);
    } else {
      download(tx, 'delivery-order');
    }
  };

  return (
    <AppShell nav={nav || adminNav} title={title}>
      <div className="space-y-4">
        {/* Banner peringatan batch belum lengkap info kirim */}
        {incompleteCount > 0 && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-3"
            data-testid="incomplete-batches-banner"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                {incompleteCount} batch pengiriman perlu perhatian
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                {incompleteTxCount} transaksi punya batch dengan info sopir/plat/alamat/tanggal
                yang belum lengkap dan sudah lebih dari 24 jam. Lengkapi sebelum dokumen dicetak.
              </div>
            </div>
            <Button
              size="sm"
              variant={showIncompleteOnly ? 'default' : 'outline'}
              onClick={() => setShowIncompleteOnly((v) => !v)}
              className={showIncompleteOnly ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-400 text-amber-800 hover:bg-amber-100'}
              data-testid="incomplete-batches-filter-toggle"
            >
              <Filter className="h-3.5 w-3.5 mr-1" />
              {showIncompleteOnly ? 'Semua Transaksi' : `Filter (${incompleteTxCount})`}
            </Button>
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative max-w-md flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cari invoice, customer, no. SO/Surat Jalan" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="docs-search" />
          </div>
          {showIncompleteOnly && (
            <Badge
              variant="outline"
              className="border-amber-400 bg-amber-50 text-amber-800 gap-1"
              data-testid="incomplete-filter-active-badge"
            >
              <Filter className="h-3 w-3" />
              Menampilkan hanya yang perlu perhatian
              <button
                className="ml-1 hover:bg-amber-100 rounded"
                onClick={() => setShowIncompleteOnly(false)}
                data-testid="incomplete-filter-clear"
              >×</button>
            </Badge>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="docs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Bayar</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>No. SO</TableHead>
                    <TableHead>No. Surat Jalan</TableHead>
                    <TableHead className="text-right">Cetak</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">Belum ada transaksi</TableCell></TableRow>
                  ) : filtered.map((t) => {
                    const remInfo = computeRemaining(t);
                    return (
                    <TableRow key={t.id} data-testid={`docs-row-${t.id}`}>
                      <TableCell className="font-mono text-xs">{t.invoice_no}</TableCell>
                      <TableCell className="text-sm">{fmtDate(t.created_at)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{t.customer_name || '-'}</div>
                        {t.customer_company && <div className="text-xs text-muted-foreground">{t.customer_company}</div>}
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px] uppercase">{t.payment_method}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums">{fmtIDR(t.total)}</TableCell>
                      <TableCell className="font-mono text-xs">{t.so_no || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="font-mono text-xs">
                        <div className="flex flex-col gap-0.5">
                          <div>{t.do_no || <span className="text-muted-foreground">-</span>}</div>
                          {remInfo && (
                            <div className="text-[10px]">
                              {remInfo.batchesCount > 1 && (
                                <Badge variant="outline" className="text-[9px] mr-1">{remInfo.batchesCount} batch</Badge>
                              )}
                              {remInfo.totalRemaining > 0 ? (
                                <span className="text-amber-700 font-medium" data-testid={`docs-remaining-${t.id}`}>
                                  ⚠ Kurang kirim: {remInfo.totalRemaining} btg
                                </span>
                              ) : (
                                <span className="text-emerald-700">✓ Lunas kirim</span>
                              )}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-col items-end gap-1">
                          {/* PDF row */}
                          <div className="inline-flex gap-1 items-center">
                            <span className="text-[10px] uppercase text-muted-foreground mr-1">PDF</span>
                            <Button size="sm" variant="outline" onClick={() => openPrint(t, 'so', false)} data-testid={`docs-so-${t.id}`} title="Pilih batch → Cetak SO PDF"><FileText className="h-3.5 w-3.5 mr-1" />SO</Button>
                            <Button size="sm" variant="outline" onClick={() => openPrint(t, 'delivery-order', false)} data-testid={`docs-do-${t.id}`} title="Pilih batch → Cetak Surat Jalan PDF"><Truck className="h-3.5 w-3.5 mr-1" />Surat Jalan</Button>
                            <Button size="sm" onClick={() => openPrint(t, 'invoice', false)} data-testid={`docs-invoice-${t.id}`} title="Pilih batch → Cetak Invoice PDF"><Receipt className="h-3.5 w-3.5 mr-1" />Invoice</Button>
                            <Button
                              size="sm" variant="outline"
                              className={remInfo && remInfo.totalRemaining > 0 ? 'border-amber-400 text-amber-700 hover:bg-amber-50' : ''}
                              onClick={() => setManageBatchTx(t)}
                              data-testid={`docs-manage-batch-${t.id}`}
                              title="Kelola batch pengiriman (list + edit info sopir/plat)"
                            >
                              <Package className="h-3.5 w-3.5 mr-1" /> Kelola Batch
                              {remInfo && remInfo.totalRemaining > 0 && (
                                <Badge className="ml-1.5 h-4 px-1 text-[9px] bg-amber-500 hover:bg-amber-500">
                                  {remInfo.totalRemaining}
                                </Badge>
                              )}
                            </Button>
                          </div>
                          {/* Dot Matrix row */}
                          <div className="inline-flex gap-1 items-center">
                            <span className="text-[10px] uppercase text-muted-foreground mr-1">Dot Matrix</span>
                            <Button size="sm" variant="ghost" className="text-primary hover:bg-primary/10" onClick={() => openPrint(t, 'so', true)} data-testid={`docs-dm-so-${t.id}`} title="Pilih batch → Cetak SO Dot Matrix"><Printer className="h-3.5 w-3.5 mr-1" />SO</Button>
                            <Button size="sm" variant="ghost" className="text-primary hover:bg-primary/10" onClick={() => openPrint(t, 'delivery-order', true)} data-testid={`docs-dm-do-${t.id}`} title="Pilih batch → Cetak Surat Jalan Dot Matrix"><Printer className="h-3.5 w-3.5 mr-1" />Surat Jalan</Button>
                            <Button size="sm" variant="ghost" className="text-primary hover:bg-primary/10" onClick={() => openPrint(t, 'invoice', true)} data-testid={`docs-dm-invoice-${t.id}`} title="Pilih batch → Cetak Invoice Dot Matrix"><Printer className="h-3.5 w-3.5 mr-1" />Invoice</Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" title="Hapus transaksi" data-testid={`docs-delete-${t.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Hapus transaksi {t.invoice_no}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Transaksi akan dihapus permanen. Stok barang otomatis dikembalikan.
                                    <br /><br />
                                    <b>Customer:</b> {t.customer_name || '-'} • <b>Total:</b> {fmtIDR(t.total)}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Batal</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={async () => {
                                      try {
                                        await api.delete(`/transactions/${t.id}`);
                                        toast.success(`Transaksi ${t.invoice_no} dihapus`);
                                        load();
                                      } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menghapus'); }
                                    }}
                                    className="bg-rose-600 hover:bg-rose-700"
                                    data-testid={`docs-delete-confirm-${t.id}`}
                                  >Ya, Hapus</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      <DeliveryDialog
        tx={deliveryFor}
        onClose={() => setDeliveryFor(null)}
        onSaved={(tx) => {
          const dmMode = deliveryFor?._dmType;
          setDeliveryFor(null);
          if (dmMode === 'delivery_order') {
            printDotMatrix(tx, 'delivery_order');
          } else {
            download(tx, 'delivery-order');
          }
        }}
      />

      {/* Dialog Kelola Batch Surat Jalan — reuse DeliveryBatchesSection dari Riwayat Transaksi. */}
      <Dialog open={!!manageBatchTx} onOpenChange={(o) => { if (!o) { setManageBatchTx(null); load(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="docs-manage-batch-dialog">
          <DialogHeader>
            <DialogTitle>Kelola Surat Jalan — {manageBatchTx?.invoice_no}</DialogTitle>
          </DialogHeader>
          {manageBatchTx && (
            <DeliveryBatchesSection txId={manageBatchTx.id} invoiceNo={manageBatchTx.invoice_no} />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Cetak per Batch — unified untuk SO/SJ/Invoice, PDF/Dot Matrix */}
      <PrintBatchDialog
        tx={printDialog?.tx}
        docType={printDialog?.docType}
        useDotMatrix={!!printDialog?.useDotMatrix}
        open={!!printDialog}
        onClose={() => { setPrintDialog(null); load(); }}
      />
    </AppShell>
  );
}

function DeliveryDialog({ tx, onClose, onSaved }) {
  const [deliveryDate, setDeliveryDate] = useState('');
  const [address, setAddress] = useState('');
  const [driver, setDriver] = useState('');
  const [plate, setPlate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tx) {
      setDeliveryDate(tx.delivery_date || '');
      setAddress(tx.delivery_address || tx.customer_name || '');
      setDriver(tx.driver_name || '');
      setPlate(tx.vehicle_plate || '');
    }
  }, [tx]);

  const save = async () => {
    if (!address || !driver || !plate) return toast.error('Alamat, sopir, dan plat wajib diisi');
    setSaving(true);
    try {
      const { data } = await api.patch(`/transactions/${tx.id}/document-info`, {
        delivery_date: deliveryDate || null,
        delivery_address: address,
        driver_name: driver,
        vehicle_plate: plate,
      });
      toast.success('Info pengiriman tersimpan, mengunduh Surat Jalan...');
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delivery-dialog" className="sm:max-w-md">
        <DialogHeader><DialogTitle>Info Pengiriman untuk Surat Jalan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground bg-muted/40 rounded p-2">Invoice: <b>{tx?.invoice_no}</b> • Customer: {tx?.customer_name}</div>
          <div className="space-y-1.5">
            <Label>Tanggal Pengiriman</Label>
            <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="delivery-date" />
          </div>
          <div className="space-y-1.5">
            <Label>Alamat Pengiriman <span className="text-rose-600">*</span></Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Alamat lengkap tujuan" data-testid="delivery-address" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nama Sopir <span className="text-rose-600">*</span></Label>
              <Input value={driver} onChange={(e) => setDriver(e.target.value)} data-testid="delivery-driver" />
            </div>
            <div className="space-y-1.5">
              <Label>Plat Kendaraan <span className="text-rose-600">*</span></Label>
              <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="B 1234 XYZ" data-testid="delivery-plate" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="delivery-save-button"><Printer className="h-4 w-4 mr-2" />{saving ? 'Menyimpan...' : 'Simpan & Cetak'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
